import CloseIcon from '@mui/icons-material/Close';
import LoopIcon from '@mui/icons-material/Loop';
import LoadingButton from '@mui/lab/LoadingButton';
import Autocomplete, {createFilterOptions} from '@mui/material/Autocomplete';
import { AppBar, Avatar, Backdrop, CircularProgress, Dialog, Grid, IconButton, MenuItem, TextField, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import moment from 'moment';
import { lighten } from 'polished';
import React, { Fragment, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import AcceptIcon from '../../icons/AcceptIcon';
import PlayIcon from '../../icons/PlayIcon';
import { MessageContext } from '../../utils/MessageContext';
import { UserContext } from '../../utils/UserContext';
import { useAxios } from '../../utils/useAxios';
import SoundDialog from '../SoundDialog';
import GoshClock from '../clock/GoshClock';
import OverlapDialog from './OverlapDialog';

const DashboardDrawer = forwardRef((props, ref) => {

    const theme = useTheme();
    const belowsm = useMediaQuery(theme.breakpoints.down("sm"));

    const location = useLocation();
    const navigate = useNavigate();

    const { t } = useTranslation();

    const { user, setUser } = useContext(UserContext);
    const { messages, setMessages } = useContext(MessageContext);

    //dialog
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [shouldUpdate, setShouldUpdate] = useState(true);

    //message
    const [message, setMessage] = useState("");
    const [announcementText, setAnnouncementText] = useState(t("no-announcement-text"));
    const [representations, setRepresentations] = useState([]);
    const [virtualRepresentations, setVirtualRepresentations] = useState([]);
    const [representative, setRepresentative] = useState("");
    const [startDate, setStartDate] = useState(moment());
    const [endDate, setEndDate] = useState(moment());
    const [type, setType] = useState({
        "representation": false,
        "virtualRepresentation": false,
        "endDateTime": true,
        "endDate": false,
        "timeOnly": false
    });
    const [representativeError, setRepresentativeError] = useState(false);
    const [messageError, setMessageError] = useState(false);
    const [recurringValue, setRecurringValue] = useState(0);
    const [fetching, setFetching] = useState(false);
    const [attempted, setAttempted] = useState(false);
    const [title, setTitle] = useState("");

    //sound
    const [audioLoading, setAudioLoading] = useState(false);
    // const [url, setUrl] = useState("");

    //overlaps
    const [openDialog, setOpenDialog] = useState(false);
    const [entry, setEntry] = useState({});
    const [overlaps, setOverlaps] = useState([]);

    //quickStarts
    const [quickStart, setQuickStart] = useState();
    const [isQuickStart, setIsQuickStart] = useState(false);
    const [quickStartLoading, setQuickStartLoading] = useState(false);
    const [newQuickStart, setNewQuickStart] = useState(false);
    const [updated, setUpdated] = useState(false);

    //edit
    const [isEdit, setIsEdit] = useState(false);
    const [oldEntry, setOldEntry] = useState({});

    const axiosInstance = useAxios();

    const titleRef = useRef("MbM");
    const soundRef = useRef(null);
    const attempt = useRef(true);

    const filterOptions = createFilterOptions({
        matchFrom: 'any',
        limit: 500,
      });

    const disableButton = () => {
        if (message === "") {
            return true;
        };
        if ((type.endDate || type.endDateTime) && endDate < startDate) {
            return true;
        }
        if (type.representation && representative === "") {
            return true;
        }
        //if startdate in the past
        if (moment().diff(startDate, "hour") > 23) {
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (user.userID !== undefined && user.userID !== "account-not-active") {
            fetchMessages();
        }
        //eslint-disable-next-line
    }, [type, user.userID, disableButton])

    
    const checkForConsistency = () => {
        const combinations = new Set();
    
        for (const m of messages) {
            const combinationKey = `${m.timeOnlyMandatory}-${m.endDateTimeMandatory}-${m.endDateMandatory}-${m.proxyMandatory}-${m.virtualRepresentation}`;
            
            if (combinations.has(combinationKey)) {
                return false;
            }
            
            combinations.add(combinationKey);
        }
    
        return true;
    };

    const fetchMessages = async () => {
        if (messages.length === 0 && attempt && !attempted) {
            setFetching(true);
            await axiosInstance.current.get("/messages/?email=" + encodeURIComponent(user?.email)).then(res => {
                if (res.data?.length > 0) {
                    setMessages(res.data);
                } else {
                    attempt.current = false;
                    setAttempted(true);
                }
            });
            setFetching(false);
        }
    };

    const fetchRepresentations = async (virtual, email) => {
        setFetching(true);
        await axiosInstance.current.get("/representations", { "params": { "virtual": virtual } }).then(res => {
            if (virtual) {
                setVirtualRepresentations([...res.data]);
            } else {
                setRepresentations([...res.data]);
            }
            if (email) {
                const match = res.data.filter(r => r.email === email)[0];
                setRepresentative(`${match?.familyName} ${match?.surName}`);
                setRepresentativeError(false);
            } else {
                if (user.defaultProxy?.email || user.defaultVirtualProxy?.email) {
                    const match = res.data.filter(r => r.email === user.defaultProxy?.email)[0];
                    const virtualMatch = res.data.filter(r => r.email === user.defaultVirtualProxy?.email)[0];
                    // console.log("match: ", match, "\nvirtualMatch: ", virtualMatch, "\ndefaultProxy: ", user.defaultProxy, "\nefaultVirtualProxy: ", user.defaultVirtualProxy, "\nvirtual: ", virtual)
                    if (!virtual) {
                        setRepresentative(match?.familyName && match?.surName ? `${match?.familyName} ${match?.surName}` : "");
                        setRepresentativeError(false);
                    }
                    if (virtual) {
                        setRepresentative(virtualMatch?.familyName && virtualMatch?.surName ? `${virtualMatch?.familyName} ${virtualMatch?.surName}` : "");
                        setRepresentativeError(false);
                    };
                }
            }
        }).catch(err => console.error(err));
        setFetching(false);
    };

    useImperativeHandle(ref, () => ({
        open(type, selectionType) {
            titleRef.current = document.title;
            setIsEdit(false);
            setIsQuickStart(false);
            console.log( user.quickStart);
            if (selectionType === "favourite" && ( user?.quickStart?.length < 5 || user?.quickStart === undefined)) {
                setNewQuickStart(true);
                document.title = t("vmm-new-favourite");
            } else {
                setNewQuickStart(false);
                document.title = t("vmm-new-abscense");
            }
            setQuickStart(undefined);
            if (messages?.length > 0) {
                setOpen(true);
                if (type) {
                    const match = messages.filter(m => (
                        m?.timeOnlyMandatory === type?.timeOnly &&
                        m?.endDateMandatory === type?.endDate &&
                        m?.endDateTimeMandatory === type?.endDateTime &&
                        m?.proxyMandatory === type?.representation &&
                        m?.virtualRepresentation === type?.virtualRepresentation
                    ))[0];
                    if (match) {
                        setMessage(match.companyMessageUid);
                        setAnnouncementText(match.announcementText);
                    }
                    if (type?.representation || type?.virtualRepresentation) {
                        fetchRepresentations(type?.virtualRepresentation);
                    } else {
                        setRepresentative("");
                    }
                    setType(type);
                    let mins = moment().minutes();
                    let hrs = moment().hours();
                    let updateStart = moment();
                    let h = mins > 45 ? (hrs === 23 ? 0 : ++hrs) : hrs;
                    updateStart.set("hour", h);
                    let quarters = [0, 15, 30, 45];
                    let m = quarters.filter(q => q > mins);
                    updateStart.set("minute", mins > 45 ? 0 : m[0]);
                    updateStart.subtract(15, "minute");
                    setStartDate(updateStart);
                    let updateEnd = moment(updateStart);
                    let diff;
                    const add15Minutes = moment().add(15, "minute");
                    diff = moment.duration(add15Minutes.diff(moment()));
                    updateEnd.add(diff);
                    setEndDate(updateEnd);
                };
            }
        },
        handleEntry(e) {
            console.log(e);
            setOldEntry(e);
            setOpen(true);
            setIsEdit(true);
            setShouldUpdate(false);
            setIsQuickStart(false);
            setNewQuickStart(false);
            setMessageError(false);
            setRepresentativeError(false);
            setUpdated(false);
            setRecurringValue(e.repeatType ?? 0);
            const obj = getMessage(e.companyMessageUid);
            setType({
                representation: obj?.proxyMandatory,
                virtualRepresentation: obj?.virtualRepresentation,
                endDateTime: obj?.endDateTimeMandatory,
                endDate: obj?.endDateMandatory,
                timeOnly: obj?.timeOnlyMandatory
            })
            setMessage(e.companyMessageUid);
            setAnnouncementText(obj?.announcementText);
            setStartDate(moment(e.startDate));
            if (e.optionalDisplayName) {
                setTitle(e.optionalDisplayName);
            } else {
                setTitle("");
            };
            if (e.endDate) {
                setEndDate(moment(e.endDate));
            } else {
                setEndDate(moment(e.startDate).add(15, "minute"));
            };
            if (obj?.proxyMandatory || obj?.virtualRepresentation) {
                fetchRepresentations(obj?.virtualRepresentation, e.representativeEmail);
            } else {
                setRepresentative("");
            };
            document.title = t("vmm-edit-voicemail");
            
            setTimeout(() => {
                setShouldUpdate(true);
            }, 100);
        },
        handleQuickStart(qs) {
            console.log(qs);
            setOpen(true);
            setIsEdit(false);
            setIsQuickStart(true);
            setShouldUpdate(false);
            setMessageError(false);
            setRepresentativeError(false);
            setUpdated(false);
            setTitle("");

            if (!qs) {
                setNewQuickStart(true);
                document.title = t("vmm-new-favourite");
            } else {
                setQuickStart(qs);
                setNewQuickStart(false);
                const obj = getMessage(qs.companyMessageUid);
                setMessage(qs.companyMessageUid);
                setAnnouncementText(obj.announcementText);
                if (obj?.proxyMandatory || obj?.virtualRepresentation) {
                    fetchRepresentations(obj?.virtualRepresentation, qs.representativeEmail);
                } else {
                    setRepresentative("");
                }
                setType({
                    representation: obj?.proxyMandatory,
                    virtualRepresentation: obj?.virtualRepresentation,
                    endDateTime: obj?.endDateTimeMandatory,
                    endDate: obj?.endDateMandatory,
                    timeOnly: obj?.timeOnlyMandatory
                })
                let mins = moment().minutes();
                let hrs = moment().hours();
                let updateStart = moment();
                let h = mins > 45 ? (hrs === 23 ? 0 : ++hrs) : hrs;
                updateStart.set("hour", h);
                let quarters = [0, 15, 30, 45];
                let m = quarters.filter(q => q > mins);
                updateStart.set("minute", mins > 45 ? 0 : m[0]);
                //change to be within current quarter
                updateStart.subtract(15, "minute");
                setStartDate(updateStart);
                let updateEnd = moment(updateStart);
                let diff;
                if (qs.endDate) {
                    diff = moment.duration(moment(qs.endDate).diff(moment(qs.startDate)));
                } else {
                    const add15Minutes = moment().add(15, "minute");
                    diff = moment.duration(add15Minutes.diff(moment()));
                }
                updateEnd.add(diff);
                setEndDate(updateEnd);
                document.title = t("vmm-new-abscense");
            }
        }
    }));

    const getMessage = (message) => {
        return messages.filter(m => m.companyMessageUid === message)[0];
    };

    const getEntry = () => {
        let announcementName = message;
        let messageObj = getMessage(message);
        let representation = representative;
        let representationObj = representations.filter(r => r.familyName + " " + r.surName === representative)[0]
        let entry = {
            id: 0,
            announcementName: announcementName,
            companyMessageUid: messageObj.companyMessageUid,
            startDate: startDate.toISOString(),
            repeatType: parseInt(recurringValue),
            notificationType: user.notificationType
        };
        if (title?.length > 0) {
            entry = { ...entry, optionalDisplayName: title };
        }
        if (type.endDateTime || type.timeOnly) {
            entry = { ...entry, endDate: endDate.toISOString() };
        }
        if (type.endDate) {
            const setHoursAndMinutesEmptyForStartDate = moment(startDate).set("hour", 0);
            setHoursAndMinutesEmptyForStartDate.set("minute", 0);
            const setHoursAndMinutesEmpty = moment(endDate).set("hour", 0);
            setHoursAndMinutesEmpty.set("minute", 0);
            entry = { ...entry, startDate: setHoursAndMinutesEmptyForStartDate.toISOString(), endDate: setHoursAndMinutesEmpty.toISOString() }
        }
        if (type.representation || type.virtualRepresentation) {
            entry = { ...entry, representation: representation, representativeEmail: representationObj?.email }
        }

        return entry;
    }

    const createEntry = async () => {

        const entry = getEntry();

        //save entry and, if successful, fetch user to set entry id
        setLoading(true)
        await axiosInstance.current({
            url: "/entry",
            method: "PUT",
            data: entry
        }).then((res) => {
            if (res.data?.length === 0) {
                axiosInstance.current.get("/user").then((res) => {
                    if (res.status === 200) {
                        setUser(res.data);
                    }
                })
            } else {
                setOpenDialog(true);
                setOverlaps(res.data);
                setEntry(entry);
            }
        }).catch(err => {
            console.log("axios err: ", err);
        })
        setLoading(false);
        setIsEdit(false);
        setQuickStart(undefined);
        setIsQuickStart(false);
        setNewQuickStart(false);
        setUpdated(false);
        setOpen(false);
        if (location.pathname === "/select") {
            navigate("/dashboard");
        }
    }

    const editEntry = async () => {
        const oldEntries = [];
        oldEntries.push(oldEntry);
        const entry = getEntry();

        await axiosInstance.current({
            url: "/entry",
            method: "DELETE",
            data: oldEntries
        }).then((res) => {
            if (res.status === 200) {
                axiosInstance.current({
                    url: "/entry",
                    method: "PUT",
                    data: entry
                }).then((res) => {
                    if (res.data?.length === 0) {
                        axiosInstance.current.get("/user").then((res) => {
                            if (res.status === 200) {
                                setUser(res.data);
                            }
                        })
                    } else {
                        setOpenDialog(true);
                        setOverlaps(res.data);
                        setEntry(entry);
                    }
                })
            }
        })
        setLoading(false);
        setIsEdit(false);
        setQuickStart(undefined);
        setIsQuickStart(false);
        setNewQuickStart(false);
        setUpdated(false);
        setOpen(false);
        if (location.pathname === "/select") {
            navigate("/dashboard");
        }
    }

    const getNewQuickStartObj = () => {
        let announcementName = message;

        let messageObj = getMessage(message);

        if (!messageObj?.companyMessageUid) {
            console.log("is undefined");
            return undefined;
        }

        let representation = representative;

        let representationObj = representations.filter(r => r.familyName + " " + r.surName === representative)[0]

        let adjustedStartDate = startDate;
        let adjustedEndDate = endDate;
        let newQuickStart = {
            announcementName: announcementName,
            companyMessageUid: messageObj.companyMessageUid,
            startDate: adjustedStartDate.toISOString(),
            endDate: null
        };

        if (type.endDateTime || type.timeOnly) {
            newQuickStart = { ...newQuickStart, endDate: adjustedEndDate.toISOString() }
        }
        if (type.endDate) {
            const setHoursAndMinutesEmptyForStartDate = moment(adjustedStartDate).set("hour", 0);
            setHoursAndMinutesEmptyForStartDate.set("minute", 0);
            const setHoursAndMinutesEmptyForEndDate = moment(adjustedEndDate).set("hour", 0);
            setHoursAndMinutesEmptyForEndDate.set("minute", 0);
            newQuickStart = { ...newQuickStart, startDate: setHoursAndMinutesEmptyForStartDate.toISOString(), endDate: setHoursAndMinutesEmptyForEndDate.toISOString() }
        }
        if (type.representation || type.virtualRepresentation) {
            newQuickStart = { ...newQuickStart, representation: representation, representativeEmail: representationObj?.email }
        }

        return newQuickStart;
    }

    const updateQuickStart = async () => {

        const newQuickStart = getNewQuickStartObj();

        setQuickStartLoading(true)
        await axiosInstance.current({
            url: "/quick-start",
            method: "PUT",
            data: quickStart ? [{ ...newQuickStart }, { ...quickStart }] : [{ ...newQuickStart }]
        }).then((res) => {
            if (res.data?.length >= 0) {
                setUser({
                    ...user,
                    quickStart: res.data
                })
            }
            setOpen(false);
            setIsEdit(false);
            setQuickStart(undefined);
            setIsQuickStart(false);
            setNewQuickStart(false);
            setUpdated(false);
        }).catch(err => {
            console.log("axios err: ", err);
        })
        setQuickStartLoading(false);
        setMessageError(false);
        setRepresentativeError(false);
        if (location.pathname === "/select") {
            navigate("/dashboard");
        }
    }

    const shouldFetchRepresentations = (obj) => {
        // if empty list --> should fetch
        if (obj?.proxyMandatory && representations.length === 0) {
            return true;
        }
        if (obj?.virtualRepresentation && virtualRepresentations.length === 0) {
            return true;
        }
        // if representative already given and of same kind --> shouldn't fetch
        if (obj?.proxyMandatory && user.defaultProxy?.userID && representative === `${user.defaultProxy.familyName} ${user.defaultProxy.surName}`) {
            return false;
        }
        if (obj?.virtualRepresentation && user.defaultVirtualProxy?.userID && representative === `${user.defaultVirtualProxy.familyName} ${user.defaultProxy.surName}`) {
            return false;
        }
    };

    const check = (event) => {
        const obj = getMessage(event.target.value);
        if (obj?.proxyMandatory || obj?.virtualRepresentation) {
            if (shouldFetchRepresentations(obj)) {
                fetchRepresentations(obj?.virtualRepresentation, false);
            };
            if (obj?.proxyMandatory) {
                const match = representations.filter(r => r.email === user.defaultProxy?.email)[0];
                setRepresentative(match?.familyName && match?.surName ? `${match?.familyName} ${match?.surName}` : "");
                setRepresentativeError(false);
            };
            if (obj?.virtualRepresentation) {
                const virtualMatch = virtualRepresentations.filter(r => r.email === user.defaultVirtualProxy?.email)[0];
                setRepresentative(virtualMatch?.familyName && virtualMatch?.surName ? `${virtualMatch?.familyName} ${virtualMatch?.surName}` : "");
                setRepresentativeError(false);
            };
        };
        if (!obj?.proxyMandatory && !obj?.virtualRepresentation) {
            setRepresentative("");
        }
        setShouldUpdate(true);
        if (quickStart) {
            setUpdated(true);
        }
        setType({
            representation: obj?.proxyMandatory,
            virtualRepresentation: obj?.virtualRepresentation,
            endDateTime: obj?.endDateTimeMandatory,
            endDate: obj?.endDateMandatory,
            timeOnly: obj?.timeOnlyMandatory
        })
        setAnnouncementText(obj?.announcementText);
        setAudioLoading(false);
        setMessage(event.target.value);
        setMessageError(false);
        setRepresentativeError(false);
    };

    const fetchSound = async () => {
        setAudioLoading(true);
        let messageObj = getMessage(message);

        console.log("messageObj: ", messageObj, "\nrepresentative: ", representative, "\nstartDate: ", startDate, "\nendDate: ", endDate, "\ntype: ", type, "\nrepresentations: ", representations, "\nvirtualRepresentations: ", virtualRepresentations)
        console.log("representations: ", representations, "\nvirtualRepresentations: ", virtualRepresentations, "\nrepresentative: ", representative)

        let representationList
        if (type.virtualRepresentation) {
            representationList = virtualRepresentations
        } else {
            representationList = representations
        }

        let representationObj = representationList.filter(r => r?.familyName + " " + r?.surName === representative)[0]

        let entry = {
            companyMessageUid: messageObj?.companyMessageUid,
            startDate: startDate?.toISOString(),
            endDate: endDate?.toISOString()
        };

        if (type.representation || type.virtualRepresentation) {
            entry = { ...entry, representativeEmail: representationObj?.email }
        }

        await axiosInstance.current({
            url: "sound-preview",
            headers: {
                ...axiosInstance.current.headers,
                "Cache-Control": "no-cache, no-store, must-revalidate"
            },
            method: "POST",
            data: entry
        }).then(res => {
            soundRef.current.open(res.data);
            setAudioLoading(false);
        }).catch(err => {
            console.error(err.response);
            setAudioLoading(false);
        })
    };

    useEffect(() => {
        // Preselect representative if only one entry exists in either representations or virtualRepresentations
        if (type.virtualRepresentation && virtualRepresentations.length === 1) {
            setRepresentative(`${virtualRepresentations[0].familyName} ${virtualRepresentations[0].surName}`);
            setRepresentativeError(false);
        } else if (type.representation && representations.length === 1) {
            setRepresentative(`${representations[0].familyName} ${representations[0].surName}`);
            setRepresentativeError(false);
        }
    }, [type, representations, virtualRepresentations]);

    return (
        <Fragment>
            <Dialog
                open={open}
                maxWidth="xs"
                fullScreen={belowsm}
                onClose={() => {
                    setOpen(false);
                    setQuickStart(undefined);
                    document.title = titleRef.current;
                    if (location.pathname === "/select") {
                        navigate("/dashboard");
                    }
                }}
                sx={{
                    "& .MuiDialog-paper": {
                        backgroundImage: "none",
                        borderRadius: belowsm ? 0 : "16px",
                        "::-webkit-scrollbar": { display: "none" }
                    }
                }}
            >
                <AppBar position="sticky" color="inherit" sx={{ width: "inherit", boxShadow: "none", bgcolor: "background.paper", backgroundImage: "none" }}>
                    <Grid container sx={{ p: 2, pb: belowsm ? 1.5 : 1, pr: 1, borderBottom: 0, borderColor: "divider" }}>
                        <Typography noWrap sx={{ color: "text.primary", fontSize: 32, fontWeight: 700, letterSpacing: "-0.8px", lineHeight: "normal", width: "85%" }} >{isEdit ? t("edit-voicemail") : newQuickStart ? t("new-favorite") : t("new-voicemail")}</Typography>
                        <Box key="spacer" sx={{ flexGrow: 1 }} />
                        <Tooltip title={t("close")} placement="right">
                            <IconButton
                                onClick={() => {
                                    setOpen(false);
                                    setQuickStart(undefined);
                                    document.title = titleRef.current;
                                    if (location.pathname === "/select") {
                                        navigate("/dashboard");
                                    }
                                }}
                                sx={{
                                    py: 0,
                                    px: 0.5,
                                    color: "text.primary",
                                    "&:hover": { color: "error.main" }
                                }}
                            >
                                <CloseIcon
                                    sx={{
                                        color: "inherit",
                                        fontSize: 30
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </AppBar>
                {/* <Chip label={t("invalid-quick-start")} color="error" size="small" variant={theme.palette.mode === "dark" ? "outlined" : "filled"} sx={{ ml: 2, mt: 1, width: "85%", display: isQuickStart && quickStart?.isStillValidAsQuickStart === false ? null : "none", borderRadius: 1 }} /> */}
                <Box sx={{ px: 2 }}>
                    <TextField
                        variant="outlined"
                        id="Message"
                        label={t("message-type")}
                        autoFocus={message === ""}
                        fullWidth
                        select
                        value={message}
                        onChange={(event) => { check(event) }}
                        onBlur={() => {
                            if (message === "") {
                                setMessageError(true)
                            }
                        }}
                        SelectProps={{
                            MenuProps: {
                                sx: {
                                    "& .MuiMenu-paper": {
                                        bgcolor: "background.default",
                                        backgroundImage: "none",
                                    }
                                }
                            }
                        }}
                        error={messageError}
                        sx={{ mt: 1 }}
                        disabled={user.userID === undefined || user.userID === "account-not-active"}
                    >
                        {messages?.map((m, i) => (
                            <MenuItem key={`item-${i}`} value={m.companyMessageUid} style={{ whiteSpace: "normal" }}>
                                <Grid container direction="column">
                                   {checkForConsistency() ? 
                                   <Typography noWrap sx={{ color: "text.primary", fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "normal" }}>{m.timeOnlyMandatory ? t("time-announcement") : m.endDateTimeMandatory ? t("datetime-announcement") : m.endDateMandatory ? t("date-announcement") : t("without-enddate")}</Typography> 
                                   : 
                                   <Typography noWrap sx={{ color: "text.primary", fontSize: "16px", fontStyle: "normal", fontWeight: 400, lineHeight: "normal" }}>{m.announcementName}</Typography>
                                   }
                                    
                                    <Grid container columnGap={0.5}>
                                        <Typography noWrap sx={{ color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : theme.palette.text.primary, fontSize: "12px", fontStyle: "normal", fontWeight: m.virtualRepresentation ? 400 : 700, lineHeight: "normal" }}>{m.virtualRepresentation ? t("office-representation") : m.proxyMandatory ? t("with") : t("without")}</Typography>
                                        <Typography noWrap sx={{ color: theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : theme.palette.text.secondary, fontSize: "12px", fontStyle: "normal", fontWeight: 400, lineHeight: "normal" }}>{m.virtualRepresentation ? "" : t("representation")}</Typography>
                                    </Grid>
                                </Grid>
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Box sx={{ px: 2, pt: belowsm ? 2 : 3, borderBottom: 0, borderColor: "divider", display: newQuickStart ? "none" : "block" }}>
                    <TextField
                        variant="outlined"
                        label={t("title")}
                        value={title}
                        onChange={(event) => { setTitle(event.target.value); }}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>
                <Box sx={{ pr: 1, pt: belowsm ? 1 : 2, pb: belowsm ? 1 : 2, borderTop: 0, borderBottom: 0, borderColor: "divider" }}>
                    <GoshClock type={type.endDate} endDateTime={type.endDateTime} timeOnlyMandatory={type.timeOnly} startValue={message !== "" ? startDate : undefined} setStartValue={setStartDate} endValue={message !== "" ? endDate : undefined} setEndValue={setEndDate} shouldUpdate={shouldUpdate} setUpdated={quickStart ? setUpdated : undefined} />
                </Box>
                <Box sx={{ px: 2, pb: !type.representation && !type.virtualRepresentation ? 1 : 0, borderBottom: 0, borderColor: "divider" }}>
                    <TextField
                        variant="outlined"
                        select
                        label={t("repetition")}
                        fullWidth
                        value={recurringValue}
                        onChange={(event) => { setRecurringValue(event.target.value); }}
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                            MenuProps: {
                                sx: {
                                    "& .MuiMenu-paper": {
                                        bgcolor: "background.default",
                                        backgroundImage: "none"
                                    }
                                }
                            }
                        }}
                        sx={{
                            "& .MuiSvgIcon-root.Mui-disabled": {
                                color: "background.default"
                            }
                        }}
                    >
                        <MenuItem key={"never"} value={0}>
                            <Typography>
                                {t("never")}
                            </Typography>
                        </MenuItem>
                        <MenuItem key={"daily-without-weekend"} value={1}>
                            <Typography>
                                {t("daily-without-weekend")}
                            </Typography>
                        </MenuItem>
                        <MenuItem key={"daily-with-weekend"} value={2}>
                            <Typography>
                                {t("daily-with-weekend")}
                            </Typography>
                        </MenuItem>
                        <MenuItem key={"weekly"} value={3}>
                            <Typography>
                                {t("weekly")}
                            </Typography>
                        </MenuItem>
                        {/* <MenuItem key={"monthly-on-day"} value={4}>
                            <Typography>
                                {t("monthly-on-day")}
                            </Typography>
                        </MenuItem>
                        <MenuItem key={"monthly-on-date"} value={5}>
                            <Typography>
                                {t("monthly-on-date")}
                            </Typography>
                        </MenuItem> */}
                        <MenuItem key={"annually"} value={6}>
                            <Typography>
                                {t("annually")}
                            </Typography>
                        </MenuItem>
                    </TextField>
                </Box>
                { 
                    console.log("representations: ", representations, "\nvirtualRepresentations: ", virtualRepresentations)
                }{
                 console.log("representatitions.length: ", representations.length, "\nvirtualRepresentations.length: ", virtualRepresentations.length)
                }
                {
                    representations.length < 20 ?
                    <Box sx={{ pb: 1, pt: !type.representation && !type.virtualRepresentation ? 1 : 2, pl: 2, pr: 1, borderBottom: 0, borderColor: "divider", display: !type.representation && !type.virtualRepresentation && belowsm ? "none" : "inline" }}>
                    <TextField
                        variant="outlined"
                        id="representation"
                        label={type.representation ? t("representation") : type.virtualRepresentation ? t("office-representation") : t("no-representation")}
                        fullWidth
                        select
                        value={representative}
                        onChange={(event) => { setRepresentative(event.target.value); setRepresentativeError(false); setUpdated(true); }}
                        onBlur={() => {
                            if (representative === "") {
                                setRepresentativeError(true)
                            }
                        }}
                        error={representativeError}
                        // required={type.representation}
                        disabled={!type.representation && !type.virtualRepresentation}
                        SelectProps={{
                            MenuProps: {
                                sx: {
                                    "& .MuiMenu-paper": {
                                        bgcolor: "background.default",
                                        backgroundImage: "none"
                                    }
                                }
                            }
                        }}
                        sx={{
                            pr: 1,
                            "& .MuiSvgIcon-root.Mui-disabled": {
                                color: "background.default"
                            }
                        }}
                    >
                        {type.virtualRepresentation ?
                            virtualRepresentations?.map((r, i) => (
                                <MenuItem key={`item-${i}`} value={`${r.familyName} ${r.surName}`}>
                                    <Typography>
                                        {`${r.familyName} ${r.surName}`.length > 46 ? `${`${r.familyName} ${r.surName}`.substring(0, 46)}...` : `${r.familyName} ${r.surName}`}
                                    </Typography>
                                </MenuItem>)) :
                            representations?.map((r, i) => (
                                <MenuItem key={`item-${i}`} value={`${r.familyName} ${r.surName}`}>
                                    <Typography>
                                        {`${r.familyName} ${r.surName}`.length > 46 ? `${`${r.familyName} ${r.surName}`.substring(0, 46)}...` : `${r.familyName} ${r.surName}`}
                                    </Typography>
                                </MenuItem>
                            ))}
                    </TextField>
                    </Box>
                    :
                    <Box sx={{ pb: 1, pt: !type.representation && !type.virtualRepresentation ? 1 : 2, pl: 2, pr: 1, borderBottom: 0, borderColor: "divider", display: !type.representation && !type.virtualRepresentation && belowsm ? "none" : "inline" }}>
                    <Autocomplete
                        id="representation"
                        options={type.virtualRepresentation ? virtualRepresentations : representations}
                        getOptionLabel={(option) => `${option.familyName} ${option.surName}` }
                        value={representative ? representations.find(r => `${r.familyName} ${r.surName}` === representative) || virtualRepresentations.find(r => `${r.familyName} ${r.surName}` === representative) || null : null}
                        onChange={(event, newValue) => {
                            setRepresentative(newValue ? `${newValue.familyName} ${newValue.surName}` : "");
                            setRepresentativeError(false);
                            setUpdated(true);
                        }}
                        onBlur={() => {
                            if (representative === "") {
                                setRepresentativeError(true);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label={type.representation ? t("representation") : type.virtualRepresentation ? t("office-representation") : t("no-representation")} 
                                variant="outlined"
                                fullWidth
                                error={representativeError}
                                disabled={!type.representation && !type.virtualRepresentation}
                            />
                        )}
                        sx={{ pr: 1 }}
                        disabled={!type.representation && !type.virtualRepresentation} 
                        filterOptions={filterOptions}
                    />
                </Box>
                }
                
                <Box sx={{ display: newQuickStart ? "none" : "inline", pr: 2, ml: 2, mt: 1 }}>
                    <Box sx={{ minHeight: 105, borderRadius: "10px", bgcolor: "secondary.main" }}>
                        <Typography sx={{ color: "text.primary", fontSize: "14px", fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.4px", ml: 2, pt: 1.5, pb: .5 }}>{t("announcement-text")}</Typography>
                        <Typography sx={{ color: "text.primary", fontSize: "14px", fontWeight: 400, lineHeight: "16px", letterSpacing: "-0.4px", ml: 2, pr: 3, pb: 1 }}>{announcementText}</Typography>
                    </Box>
                </Box>
                <SoundDialog message={type.timeOnly ? t("time-announcement") : type.endDateTime ? t("datetime-announcement") : type.endDate ? t("date-announcement") : t("without-enddate")} startDate={startDate} endDate={endDate} noEnd={!type.endDate && !type.endDateTime && !type.timeOnly} noMinutes={type.endDate} ref={soundRef} />
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    <Grid container sx={{ mt: 2, mb: 1, px: 2 }}>
                        <Grid item xs={12}>
                            <Box
                                onClick={() => {
                                    if (message === "" && open) {
                                        setMessageError(true);
                                    }
                                    if (type.representation && representative === "" && open) {
                                        setRepresentativeError(true)
                                    }
                                }}
                            >
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    loading={loading}
                                    fullWidth
                                    onClick={newQuickStart ? updateQuickStart : isEdit ? editEntry : createEntry}
                                    startIcon={loading ? null : <Avatar sx={{ bgcolor: user.userID !== undefined ? "primary.contrastText" : "action.hover", ml: .15, height: 43, width: 43 }}>
                                        {disableButton() ? <AcceptIcon color={theme.palette.info.main} /> : <AcceptIcon />}
                                    </Avatar>}
                                    loadingIndicator={<CircularProgress color="inherit" size={26} />}
                                    sx={{
                                        textTransform: "none",
                                        height: 52,
                                        justifyContent: "flex-start",
                                        pl: 1,
                                        borderRadius: "26px",
                                        "&:hover": { bgcolor: lighten(0.05, theme.palette.primary.main) },
                                        "&:disabled": { backgroundColor: "action.disableBackground", borderColor: "action.hover" }
                                    }}
                                    disableElevation
                                    disabled={disableButton()}
                                >
                                    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                        <Typography sx={{ color: disableButton() ? "text.primary" : "primary.contrastText", fontSize: 22, visibility: loading ? "hidden" : "visible", display: open ? "inline" : "none", fontWeight: 700, fontStyle: "normal", letterSpacing: "-0.55px" }}>
                                            {newQuickStart ? t("save-favorite") : t("save-entry")}
                                        </Typography>
                                    </Box>
                                </LoadingButton>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <LoadingButton
                                variant="outlined"
                                color="primary"
                                fullWidth
                                onClick={fetchSound}
                                loading={audioLoading}
                                startIcon={audioLoading ? null : <Avatar sx={{ bgcolor: "transparent", border: disableButton() ? 1 : 2, borderColor: disableButton() ? "info.main" : "primary.main", height: 43, width: 43, ml: -.2 }}>
                                    {disableButton() ? <PlayIcon fontSize={20} color={theme.palette.info.main} /> : <PlayIcon fontSize={20} />}
                                </Avatar>}
                                loadingIndicator={<CircularProgress color="inherit" size={26} />}
                                sx={{
                                    textTransform: "none",
                                    height: 53,
                                    border: 2,
                                    my: belowsm ? 1 : 2,
                                    pl: 1,
                                    justifyContent: "flex-start",
                                    borderRadius: "26px",
                                    "&:hover": { border: 2 }
                                }}
                                disabled={disableButton()}
                            >
                                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                    <Typography sx={{ color: disableButton() ? "text.primary" : "primary.main", fontSize: 22, visibility: audioLoading ? "hidden" : "visible", fontWeight: 700, fontStyle: "normal", letterSpacing: "-0.55px" }}>
                                        {t("test-listen")}
                                    </Typography>
                                </Box>
                            </LoadingButton>
                        </Grid>
                        <Grid item xs={12} sx={{ display: (isQuickStart && !newQuickStart) || (user?.quickStart?.length < 5 && !newQuickStart && !isEdit) ? "inherit" : "none", pb: 2 }}>
                            <LoadingButton
                                variant="outlined"
                                color={theme.palette.mode === "light" ? "favorite" : "info"}
                                onClick={updateQuickStart}
                                fullWidth
                                startIcon={quickStartLoading ? null : <Avatar sx={{ bgcolor: updated ? "text.primary" : theme.palette.mode === "light" ? "favorite.main" : "info.main", height: 43, width: 43, ml: -.2 }}>
                                    <LoopIcon sx={{ color: "background.default", transform: "rotate(-45deg)" }} />
                                </Avatar>}
                                loading={quickStartLoading}
                                loadingIndicator={<CircularProgress color={"inherit"} size={26} />}
                                sx={{
                                    textTransform: "none",
                                    height: 53,
                                    border: 2,
                                    pl: 1,
                                    justifyContent: "flex-start",
                                    borderRadius: "26px",
                                    borderColor: updated ? "text.primary" : "inherit",
                                    "&:hover": { border: 2, borderColor: updated ? "text.primary" : "inherit" },
                                    "&.Mui-selected": { border: 2, borderColor: updated ? "text.primary" : "inherit" }

                                }}
                                disabled={disableButton()}
                            >
                                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                    <Typography sx={{ color: updated ? "text.primary" : theme.palette.mode === "light" ? "favorite.main" : "info.main", fontSize: 22, visibility: loading || quickStartLoading ? "hidden" : "visible", display: open ? "inline" : "none", fontWeight: 700, fontStyle: "normal", letterSpacing: "-0.55px" }}>
                                        {isQuickStart ? t("update-favorite") : t("save-as-favorite")}
                                    </Typography>
                                </Box>
                            </LoadingButton>
                        </Grid>
                    </Grid>
                </Box>
            </Dialog>
            <OverlapDialog
                openDialog={openDialog}
                setOpenDialog={setOpenDialog}
                entry={entry}
                overlaps={overlaps}
            />
            <Backdrop
                sx={{
                    zIndex: (theme) => Math.max.apply(Math, Object.values(theme.zIndex)) + 1,
                }}
                open={fetching}
            >
                <CircularProgress
                    size={108}
                    thickness={2}
                    sx={{ color: "#fff" }}
                />
            </Backdrop>
        </Fragment>
    )
});

export default DashboardDrawer;